<script setup>
import { NButton, NMenu, NIcon, NText } from 'naive-ui'
import { h, computed, ref, onMounted, inject } from 'vue'
import ArsLogoLight from '@/assets/images/logo3.png'
import { SignOutAlt } from '@vicons/fa'
import { useRoute, RouterLink, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { log } from '@/services/log'

const props = defineProps({
  vertical: Boolean,
  isNavTop: Boolean,
})

const mode = computed(() => (props.vertical ? 'vertical' : 'horizontal'))

const appStoreFn = inject('appStoreFn')
const appStore = appStoreFn()

const authStore = useAuthStore()
const router = useRouter()

const route = useRoute()
const requiresAuth = computed(() => route?.meta?.requiresAuth ?? false)
const injectedMenuOptions = inject('menuOptions') ?? []

const logoImage = ref(null)
onMounted(() => {
  logoImage.value = ArsLogoLight
})

const userDisplayName = computed(() => authStore?.user?.username ?? 'Tech')
const userInitial = computed(() => (userDisplayName.value ? userDisplayName.value.charAt(0).toUpperCase() : 'T'))

const renderIcon = (option) => {
  if (!option.icon) return null
  return h(NIcon, null, { default: option.icon })
}

const renderLabel = (option) => {
  if (option?.to) return h(RouterLink, { to: option.to }, { default: () => option.label })
  else if (option.key === 'logo') {
    if (!props.isNavTop) {
      return h(
        'div',
        { class: 'nav-brand' },
        [
          h('div', { class: 'nav-brand-main' }, [
            h('div', { class: 'nav-brand-avatar' }, userInitial.value),
            h('div', { class: 'nav-brand-text' }, [
              h('span', { class: 'nav-brand-title' }, 'WELCOME'),
              h(
                'span',
                {
                  class: 'nav-brand-subtitle',
                  style: 'color: #93c5fd !important; font-weight: 700;',
                },
                userDisplayName.value
              ),
            ]),
            h(
              NButton,
              {
                class: 'nav-brand-logout-icon',
                ghost: true,
                size: 'tiny',
                type: 'primary',
                circle: true,
                onClick: () => authStore.logout(),
              },
              {
                icon: () => h(NIcon, null, { default: () => h(SignOutAlt) }),
              }
            ),
          ]),
        ]
      )
    }

    return h(
      'div',
      { class: 'nav-brand' },
      [
        h('img', {
          src: logoImage.value,
          alt: 'brand logo',
          class: 'nav-brand-image',
        }),
        appStore.showWelcomeTitle && !props.isNavTop
          ? h('div', { class: 'nav-brand-text' }, [
              h('span', { class: 'nav-brand-title' }, 'WELCOME'),
              h(
                'span',
                {
                  class: 'nav-brand-subtitle',
                  style: 'color: #93c5fd !important; font-weight: 700;',
                },
                userDisplayName.value
              ),
            ])
          : null,
      ]
    )
  }
  else return h(NText, { type: 'span' }, { default: () => option.label })
}

const filterMenuOptions = (options, canSee, isTechnician) => {
  return options
    .map((option) => {
      if (option.items) {
        const filteredItems = filterMenuOptions(option.items, canSee, isTechnician)
        if (filteredItems.length > 0) {
          return { ...option, items: filteredItems }
        }
      } else if (option.checkPermission === false || isTechnician) {
        return option
      } else if (option.permissionUri && canSee(option?.permissionUri)) {
        return option
      }
      return null
    })
    .filter(Boolean)
}

const menuOptions = computed(() => {
  const isTechnician = authStore?.user?.isTechnician ?? false
  return [
    ...filterMenuOptions(injectedMenuOptions, authStore.canSee, isTechnician),
  ]
})

const handleUserMenu = (key) => {
  const actions = {
    profile: () => {
      const externalId = authStore.user.externalId
      const path = `/users/mono?q=${externalId}`
      router.push(path)
    },
    changePassword: () => {
      appStore.userResetInfo = authStore.user
      appStore.showResetPasswordModal = true
    },
    about: () => appStore.setAboutModal(true),
    logout: () => authStore.logout(),
  }
  const action = actions[key]
  if (action) {
    action()
  } else {
    log(`Unhandled key: ${key}, in handleUserMenu trigger in navigation menu`)
  }
}
</script>

<template>
  <div id="arsSiteMenuWrapper">
    <div v-if="requiresAuth && !props.isNavTop" class="nav-brand">
      <div class="nav-brand-main">
        <div class="nav-brand-avatar">{{ userInitial }}</div>
        <div class="nav-brand-text">
          <span class="nav-brand-title">WELCOME</span>
          <span class="nav-brand-subtitle">{{ userDisplayName }}</span>
        </div>
        <NButton
          class="nav-brand-logout-icon"
          ghost
          size="tiny"
          type="primary"
          circle
          @click="authStore.logout()"
        >
          <NIcon>
            <SignOutAlt />
          </NIcon>
        </NButton>
      </div>
    </div>

    <NMenu
      id="arsSiteMenu"
      v-if="requiresAuth"
      :options="menuOptions"
      children-field="items"
      :render-icon="renderIcon"
      :render-label="renderLabel"
      :mode="mode"
      @update:value="handleUserMenu"
    />
    <div v-if="requiresAuth && !props.isNavTop" class="nav-footer">
      <img
        :src="logoImage"
        alt="American RFID Solutions"
        class="nav-footer-logo"
      />
    </div>
  </div>
</template>

<style>
  #arsSiteMenuWrapper {
    position: relative;
    min-height: 100vh;
    box-sizing: border-box;
    padding-bottom: 4rem;
  }

  #arsSiteMenu {
    position: relative;
    padding: 1.5rem 0.5rem 1.5rem;
  }

  #arsSiteMenu > .n-menu-item:last-child {
    position: absolute;
    bottom: 1.5rem;
    width: calc(100% - 1rem);
  }

  .nav-brand {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
    padding: 0.85rem 0.45rem 0.85rem;
    color: #ffffff;
    width: 100%;
    box-sizing: border-box;
    background: transparent !important;
    pointer-events: none;
  }

  .nav-brand-main {
    display: flex;
    align-items: flex-end;
    gap: 0.75rem;
    width: 100%;
  }

  .nav-brand-avatar {
    width: 50px;
    height: 50px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #1d4ed8;
    color: #ffffff;
    font-weight: 700;
    font-size: 1rem;
  }

  .nav-brand-text {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.1rem;
    flex: 1;
    min-width: 0;
  }

  .nav-brand-logout-icon {
    margin-left: auto;
    margin-top: 0.35rem;
    width: 1.7rem;
    height: 1.7rem;
    min-width: auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #93c5fd !important;
    border-color: rgba(255, 255, 255, 0.18) !important;
    background: rgba(255, 255, 255, 0.05) !important;
    box-shadow: none;
    padding: 0;
    pointer-events: auto;
  }

  .nav-brand-logout-icon:hover {
    background: rgba(255, 255, 255, 0.12) !important;
  }

  .nav-brand-image {
    width: 46px;
    height: 46px;
    object-fit: contain;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.25);
    background: rgba(255, 255, 255, 0.08);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
  }

  .nav-brand-text {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.125rem;
  }

  .nav-brand-title {
    display: block;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #ffffff;
  }

  .nav-footer {
    position: absolute;
    left: 0;
    bottom: 1.2rem;
    width: 100%;
    display: flex;
    align-items: flex-end;
    justify-content: flex-start;
    pointer-events: none;
  }

  .nav-footer-logo {
    width: 87%;
    max-width: none;
    height: auto;
    object-fit: contain;
    background: transparent;
    display: block;
    opacity: 0.98;
  }

  .nav-brand-subtitle {
    display: block;
    font-size: 0.95rem;
    font-weight: 600;
    color: #93c5fd !important;
  }

  #arsSiteMenu .nav-brand-subtitle {
    color: #93c5fd !important;
  }

  #arsSiteMenu .n-menu-item {
    border-radius: 1rem;
    margin: 0.35rem 0;
    padding: 0.8rem 1rem;
  }


  #arsSiteMenu .n-menu-item .n-menu-item-content,
  #arsSiteMenu .n-menu-item .n-menu-item-content span,
  #arsSiteMenu .n-menu-item .n-menu-item__content,
  #arsSiteMenu .n-menu-item .n-menu-item__content span,
  #arsSiteMenu .n-menu-item .n-menu-item-content-header a,
  #arsSiteMenu .n-menu-item .n-menu-item-content-header a span {
    color: rgba(255, 255, 255, 0.75) !important;
  }

  #arsSiteMenu .n-menu-item:hover .n-menu-item-content,
  #arsSiteMenu .n-menu-item-active .n-menu-item-content,
  #arsSiteMenu .n-menu-item:hover .n-menu-item__content,
  #arsSiteMenu .n-menu-item-active .n-menu-item__content,
  #arsSiteMenu .n-menu-item:hover .n-menu-item-content-header a,
  #arsSiteMenu .n-menu-item-active .n-menu-item-content-header a {
    color: #ffffff !important;
  }

  #arsSiteMenu .n-menu-item:hover,
  #arsSiteMenu .n-menu-item-active {
    background: transparent !important;
  }

  #arsSiteMenu .n-menu-item:hover .n-menu-item-content::before,
  #arsSiteMenu .n-menu-item.hover .n-menu-item-content::before,
  #arsSiteMenu .n-menu-item-active .n-menu-item-content::before {
    background-color: rgba(255, 255, 255, 0.08) !important;
  }

  #arsSiteMenu .n-menu-item-icon,
  #arsSiteMenu .n-menu-item-icon svg,
  #arsSiteMenu .n-menu-item-icon path,
  #arsSiteMenu .n-menu-item .n-icon,
  #arsSiteMenu .n-menu-item .n-icon svg,
  #arsSiteMenu .n-menu-item .n-icon path {
    color: #ffffff !important;
    fill: #ffffff !important;
    stroke: #ffffff !important;
  }

  #arsSiteMenu .n-menu-item-active {
    box-shadow: 0 10px 32px rgba(37, 99, 235, 0.16);
  }

  #arsSiteMenu .n-menu-item-content {
    font-weight: 500;
  }

  #arsSiteMenu .n-menu-item-content:hover {
    color: #ffffff;
  }

</style>
 